import { FetchResult } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { FormInputText } from '@pendwa/common-browser/components/Form/InputText/index.js';
import { FormSubmitButton } from '@pendwa/common-browser/components/Form/SubmitButton/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { rootRoutes } from '@pendwa/common/core/rootRoutes/index.js';
import { userSchema } from '@pendwa/db/generated/zod/index.js';
import { password_schema } from '@pendwa/db/zod/schema.js';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { HooksAccountLoginMutation } from '../../../generated/graphql.js';
import { useAccountLoginGraphql } from '../../../hooks/user/loginGraphql.js';

const schema = z
  .object({
    email: userSchema.shape.email,
    password: password_schema,
  })
  .required();

export const Login: React.FC<{
  handleSuccess: (result: FetchResult<HooksAccountLoginMutation>) => void;
}> = (props) => {
  const { result: mutation_result, doAction } = useAccountLoginGraphql();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  return (
    <LayoutSpacerSmall>
      {mutation_result.error && (
        <FormErrorMessage
          message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
        />
      )}

      <form
        onSubmit={handleSubmit(async (data) => {
          const action_result = await doAction({
            variables: {
              user_name: data.email ?? '',
              password: data.password ?? '',
            },
            errorPolicy: 'all',
          });

          if (!action_result.errors && action_result.data?.accountLogin?.data?.uuid) {
            setTimeout(() => props.handleSuccess(action_result), 500);
          }
        })}
      >
        <LayoutSpacerSmall>
          <FormInputText
            label={'Email'}
            description={'Enter your email address.'}
            input_placeholder={'you@example.com'}
            input_type={'email'}
            input_name={'email'}
            register={register}
            error={errors.email?.message}
          />

          <FormInputText
            label={'Password'}
            description={'Enter your current password.'}
            input_type={'password'}
            input_name={'password'}
            register={register}
            error={errors.password?.message}
          />

          <FormSubmitButton label={'Sign In'} loading={mutation_result.loading} />
        </LayoutSpacerSmall>
      </form>

      <div className="flex flex-col items-center justify-center">
        <p className="text-center text-sm text-gray-600">Forgot your password?</p>
        <div className="text-sm">
          <Link
            to={rootRoutes.app.account.forgotPassword.get()}
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            Reset Your Password
          </Link>
        </div>
      </div>
    </LayoutSpacerSmall>
  );
};
